import { CATEGORY_BY_ID } from '../categories';
import type { Registration } from '../registry';

const n = CATEGORY_BY_ID.network.colour;

export const networkRegistration: Registration = {
  definitions: [
    {
      type: 'esp32_on_setup',
      message0: 'when board starts %1',
      args0: [{ type: 'input_statement', name: 'DO' }],
      colour: CATEGORY_BY_ID.control.colour,
      tooltip: 'Runs once at power-up. Put configuration blocks here.',
    },
    {
      type: 'esp32_on_loop',
      message0: 'forever, every cycle %1',
      args0: [{ type: 'input_statement', name: 'DO' }],
      colour: CATEGORY_BY_ID.control.colour,
      tooltip: 'Runs repeatedly after setup. Put your main logic here.',
    },
    {
      type: 'esp32_wifi_connect',
      message0: 'WiFi connect to %1 password %2',
      args0: [
        { type: 'field_input', name: 'SSID', text: 'MyNetwork' },
        { type: 'field_input', name: 'PASSWORD', text: '' },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Join a Wi-Fi network in station mode (waits up to 15 s).',
    },
    {
      type: 'esp32_wifi_connected',
      message0: 'WiFi connected?',
      output: 'Boolean',
      colour: n,
      tooltip: 'True while the station is associated and has an IP.',
    },
    {
      type: 'esp32_wifi_local_ip',
      message0: 'WiFi local IP',
      output: 'String',
      colour: n,
      tooltip: 'The assigned IP address as text.',
    },
    {
      type: 'esp32_wifi_disconnect',
      message0: 'WiFi disconnect',
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Turn station mode off.',
    },
    {
      type: 'esp32_http_get',
      message0: 'HTTP GET %1',
      args0: [{ type: 'input_value', name: 'URL', check: 'String' }],
      output: 'String',
      colour: n,
      tooltip: 'Fetch a URL; value is the response body (empty string on failure).',
    },
    {
      type: 'esp32_http_post_json',
      message0: 'HTTP POST %1 body %2',
      args0: [
        { type: 'input_value', name: 'URL', check: 'String' },
        { type: 'input_value', name: 'BODY', check: 'String' },
      ],
      output: 'Number',
      colour: n,
      tooltip: 'POST a JSON body; value is the HTTP status code.',
    },
    {
      type: 'esp32_mqtt_setup',
      message0: 'MQTT setup host %1 port %2 user %3 password %4 client id %5',
      args0: [
        { type: 'field_input', name: 'HOST', text: 'broker.local' },
        { type: 'field_number', name: 'PORT', value: 1883, min: 1, max: 65535, precision: 1 },
        { type: 'field_input', name: 'USER', text: '' },
        { type: 'field_input', name: 'PASSWORD', text: '' },
        { type: 'field_input', name: 'CLIENT_ID', text: 'esp32-blocks' },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Configure and connect the MQTT client (PubSubClient). Wi-Fi must already be connected.',
    },
    {
      type: 'esp32_mqtt_publish',
      message0: 'MQTT publish topic %1 payload %2',
      args0: [
        { type: 'input_value', name: 'TOPIC', check: 'String' },
        { type: 'input_value', name: 'PAYLOAD' },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Publish a message to a topic.',
    },
    {
      type: 'esp32_mqtt_subscribe',
      message0: 'MQTT subscribe topic %1',
      args0: [{ type: 'input_value', name: 'TOPIC', check: 'String' }],
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Subscribe to a topic.',
    },
    {
      type: 'esp32_mqtt_loop',
      message0: 'MQTT process messages',
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Pump the MQTT client (auto-inserted in the loop when MQTT is used; use only if you need a second pump point).',
    },
    {
      type: 'esp32_mqtt_connected',
      message0: 'MQTT connected?',
      output: 'Boolean',
      colour: n,
      tooltip: 'True while the MQTT client is connected to the broker.',
    },
    {
      type: 'esp32_on_mqtt_message',
      message0: 'on MQTT message topic %1',
      args0: [{ type: 'field_input', name: 'TOPIC', text: 'home/led' }],
      message1: 'do %1',
      args1: [{ type: 'input_statement', name: 'DO' }],
      colour: n,
      tooltip: 'Runs when a message arrives on the topic. Read it with the "MQTT payload" value block.',
    },
    {
      type: 'esp32_mqtt_payload',
      message0: 'MQTT payload',
      output: 'String',
      colour: n,
      tooltip: 'The payload of the message currently being handled (valid inside "on MQTT message").',
    },
  ],
  metas: [
    {
      type: 'esp32_on_setup',
      category: 'control',
      label: 'When board starts',
      description: 'Hat: runs once at boot. Configuration blocks go here.',
      fields: {},
      statementInput: 'DO',
      hat: true,
    },
    {
      type: 'esp32_on_loop',
      category: 'control',
      label: 'Forever, every cycle',
      description: 'Hat: the main loop, runs repeatedly.',
      fields: {},
      statementInput: 'DO',
      hat: true,
    },
    {
      type: 'esp32_wifi_connect',
      category: 'network',
      label: 'WiFi connect',
      description: 'Join a Wi-Fi network (station mode) and wait up to 15 seconds.',
      fields: { SSID: { kind: 'text' }, PASSWORD: { kind: 'text' } },
    },
    {
      type: 'esp32_wifi_connected',
      category: 'network',
      label: 'WiFi connected',
      description: 'Value: true when the Wi-Fi station is connected.',
      fields: {},
    },
    {
      type: 'esp32_wifi_local_ip',
      category: 'network',
      label: 'WiFi local IP',
      description: 'Value: local IP address as text.',
      fields: {},
    },
    {
      type: 'esp32_wifi_disconnect',
      category: 'network',
      label: 'WiFi disconnect',
      description: 'Disconnect Wi-Fi and turn station mode off.',
      fields: {},
    },
    {
      type: 'esp32_http_get',
      category: 'network',
      label: 'HTTP GET',
      description: 'Value: response body of an HTTP GET (empty on failure).',
      fields: { URL: { kind: 'value', types: 'String' } },
    },
    {
      type: 'esp32_http_post_json',
      category: 'network',
      label: 'HTTP POST JSON',
      description: 'Value: HTTP status code of a JSON POST.',
      fields: { URL: { kind: 'value', types: 'String' }, BODY: { kind: 'value', types: 'String' } },
    },
    {
      type: 'esp32_mqtt_setup',
      category: 'network',
      label: 'MQTT setup',
      description: 'Configure and connect the MQTT client (host, port, optional user/password, client id).',
      fields: { HOST: { kind: 'text' }, PORT: { kind: 'number' }, USER: { kind: 'text' }, PASSWORD: { kind: 'text' }, CLIENT_ID: { kind: 'text' } },
    },
    {
      type: 'esp32_mqtt_publish',
      category: 'network',
      label: 'MQTT publish',
      description: 'Publish a payload to a topic.',
      fields: { TOPIC: { kind: 'value', types: 'String' }, PAYLOAD: { kind: 'value', types: 'any' } },
    },
    {
      type: 'esp32_mqtt_subscribe',
      category: 'network',
      label: 'MQTT subscribe',
      description: 'Subscribe to a topic.',
      fields: { TOPIC: { kind: 'value', types: 'String' } },
    },
    {
      type: 'esp32_mqtt_loop',
      category: 'network',
      label: 'MQTT process messages',
      description: 'Pump the MQTT client loop (usually automatic).',
      fields: {},
    },
    {
      type: 'esp32_mqtt_connected',
      category: 'network',
      label: 'MQTT connected',
      description: 'Value: true when the MQTT client is connected.',
      fields: {},
    },
    {
      type: 'esp32_on_mqtt_message',
      category: 'network',
      label: 'On MQTT message',
      description: 'Hat: runs when a message arrives on the topic; read the payload with the MQTT payload block.',
      fields: { TOPIC: { kind: 'text' } },
      statementInput: 'DO',
      hat: true,
    },
    {
      type: 'esp32_mqtt_payload',
      category: 'network',
      label: 'MQTT payload',
      description: 'Value: payload of the message currently being handled (only valid inside on MQTT message).',
      fields: {},
    },
  ],
};
